import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { emailBuilderSetActivePanel } from "redux/actions/emailBuilder/emailBuilder";
import type { TEmailBuilderActivePanel } from "redux/reducers/emailBuilder/emailBuilder";
import { useBuilderMenuPanelState } from "components/builderShell/hooks/useBuilderMenuPanelState";
import { useEmailBuilderViewport } from "../emailBuilderViewportContext";

export const EMAIL_BUILDER_PANEL_SLIDE_MS = 250;

export type TSetEmailBuilderActivePanel = (panel: TEmailBuilderActivePanel) => void;

export interface IEmailBuilderPanelSlideState {
  panelOpen: boolean;
  closingPanel: boolean;
  closingFromPanel: TEmailBuilderActivePanel | null;
  setActivePanel: TSetEmailBuilderActivePanel;
}

export function useEmailBuilderPanelSlide(): IEmailBuilderPanelSlideState {
  const dispatch = useAppDispatch();
  const { usesBottomSheetChrome } = useEmailBuilderViewport();
  const activePanel = useAppSelector(store => store.emailBuilderStore.activePanel);
  const loadStatus = useAppSelector(store => store.emailBuilderStore.loadStatus);
  const mode = useAppSelector(store => store.emailBuilderStore.mode);

  const setActivePanelInStore = useCallback(
    (panel: TEmailBuilderActivePanel) => {
      dispatch(emailBuilderSetActivePanel(panel));
    },
    [dispatch],
  );

  const { closing, closingFromActiveId, requestSetActiveId } = useBuilderMenuPanelState({
    activeId: activePanel,
    setActiveId: setActivePanelInStore,
    noneId: "none",
    slideMs: EMAIL_BUILDER_PANEL_SLIDE_MS,
    skipCloseAnimation: () => usesBottomSheetChrome,
    blockActionsWhileClosing: false,
  });

  return {
    panelOpen:
      loadStatus === "succeeded" &&
      mode === "design" &&
      activePanel !== "none" &&
      !closing,
    closingPanel: closing,
    closingFromPanel:
      closing && closingFromActiveId !== null && closingFromActiveId !== "none"
        ? closingFromActiveId
        : null,
    setActivePanel: requestSetActiveId,
  };
}
